import { useMutation, useReactiveVar } from '@apollo/client';
import { READ_BENEFITS } from '@queries/readBenefits.tsx';
import { Button, Meta } from 'aimo-ui';
import SuccessIndicator from 'components/common/SuccessIndicator';
import AccountContext from 'context/AccountContext';
import { REDEEM_MG_UNLOCK_CODE } from 'graphql/mutations/redeemMemberGroupUnlockCode';
import { GET_UNIFIED_PERMITS } from 'graphql/queries/getUnifiedPermits';
import useIsLoggedIn from 'hooks/useIsLoggedIn';
import React, { useCallback, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { View } from 'react-native';
import { unlockMemberGroupSuccess } from 'states/common';
import { usePermitStore } from 'stores/permitStore';
import { StyledWrapper } from 'styles/ContainerStyles';
import {
  Mutation,
  MutationRedeemUnlockCodeArgs,
} from 'types/generatedSchemaTypes';
import StaffMemberSignInPrompt from './StaffMemberSignInPrompt';
import UnlockPmcBase from './UnlockPmcBase';
import { useUiStore } from 'stores/uiStore';

const UnlockMemberGroupModalContent: React.FC = () => {
  const { t } = useTranslation();
  const { isLoggedIn } = useIsLoggedIn();
  const { refetchPermits } = useContext(AccountContext);
  const [unlockCode, setUnlockCode] = useState('');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const isSuccess = useReactiveVar(unlockMemberGroupSuccess);

  const selectedParkZoneBenefitValue = usePermitStore(
    (state) => state.selectedParkZoneBenefit
  );
  const benefitUid = selectedParkZoneBenefitValue?.benefitUid;
  const benefitTargetUid = selectedParkZoneBenefitValue?.benefitTargetUid;
  const benefitTargetType = selectedParkZoneBenefitValue?.benefitTargetType;

  useEffect(() => {
    unlockMemberGroupSuccess(false);
  }, []);

  const handleClose = useCallback(() => {
    unlockMemberGroupSuccess(false);
    useUiStore.getState().setSheetModal('');
  }, []);

  const [redeemUnlockCode, { loading }] = useMutation<
    Mutation,
    MutationRedeemUnlockCodeArgs
  >(REDEEM_MG_UNLOCK_CODE, {
    refetchQueries: [
      {
        query: READ_BENEFITS,
      },
      {
        query: GET_UNIFIED_PERMITS,
      },
    ],
    awaitRefetchQueries: true,
    onCompleted: (data) => {
      if (
        data?.redeemUnlockCode?.status === 'UNLOCK_CODE_REDEEM_DUPLICATE' ||
        data?.redeemUnlockCode?.status === 'UNLOCK_CODE_REDEEMED'
      ) {
        // Permit creation is async (membership → SQS); delayed refetch covers lag.
        setTimeout(() => {
          void refetchPermits?.();
        }, 3000);
        unlockMemberGroupSuccess(true);
      } else {
        setErrorMessage(
          t('longTermParking.staffMemberPermit.unlock.incorrect')
        );
      }
    },
    onError: (error) => {
      setErrorMessage(
        error.networkError
          ? t('error.genericError')
          : t('longTermParking.staffMemberPermit.unlock.incorrect')
      );
    },
  });

  const handleInputChange = useCallback((text: string) => {
    setErrorMessage(null);
    setUnlockCode(text);
  }, []);

  const handleUnlock = useCallback(async () => {
    setErrorMessage(null);

    if (!benefitUid || !benefitTargetUid || !benefitTargetType) {
      setErrorMessage(t('error.genericError'));
      return;
    }

    await redeemUnlockCode({
      variables: {
        benefitUid,
        benefitTargetType,
        benefitTargetUid,
        targetType: 'MEMBER_GROUP',
        input: {
          code: unlockCode,
        },
      },
    });
  }, [
    benefitUid,
    benefitTargetUid,
    benefitTargetType,
    redeemUnlockCode,
    unlockCode,
    t,
  ]);

  if (!isLoggedIn) {
    return <StaffMemberSignInPrompt testID="unlock-member-group-login" />;
  }

  if (isSuccess) {
    return (
      <View>
        <StyledWrapper mt={30} mb={0}>
          <SuccessIndicator
            message={t('longTermParking.staffMemberPermit.successMessage')}
            testID="unlock-member-group-success"
          />
        </StyledWrapper>
        <StyledWrapper mb={60} mt={0}>
          <Meta>
            {t('longTermParking.staffMemberPermit.successDescription')}
          </Meta>
        </StyledWrapper>
        <Button
          size="l"
          onPress={handleClose}
          text={t('button.close')}
          testID="unlock-member-group-close-button"
          buttonType="secondary"
        />
      </View>
    );
  }

  return (
    <UnlockPmcBase
      unlockCode={unlockCode}
      errorMessage={errorMessage}
      loading={loading}
      onUnlock={handleUnlock}
      onCancel={handleClose}
      onInputChange={handleInputChange}
      description={t('longTermParking.staffMemberPermit.unlock.description')}
      codeFieldTitle={t(
        'longTermParking.staffMemberPermit.unlock.codePlaceholder'
      )}
      primaryButtonText={t('longTermParking.staffMemberPermit.unlock.verify')}
    />
  );
};

export default UnlockMemberGroupModalContent;
